import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { CHURCH, LOGO_URL } from '@/api/client';
import EventsCarousel from '@/components/home/EventsCarousel';
import SectionTitle from '@/components/SectionTitle';
import Seo from '@/seo/Seo';
import { PAGE_SEO, BUSINESS } from '@/seo/seoConfig';
import { breadcrumbSchema, faqSchema, recurringServiceEvent } from '@/seo/schemas';
import { Play, Heart, Sparkles, Users, BookOpen, HandHeart, ArrowRight, Youtube, Calendar, Video, Sun, Clock, MapPin } from 'lucide-react';

function decodeEntities(str) {
  if (!str) return '';
  return str
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `${mins} min${mins > 1 ? 's' : ''} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} wk${weeks > 1 ? 's' : ''} ago`;
  return d.toLocaleDateString();
}

export default function Home() {
  const { data: events = [] } = useQuery({ queryKey: ['public-events'], queryFn: async () => (await api.get('/public/events')).data });
  const { data: ytData } = useQuery({ queryKey: ['public-yt'], queryFn: async () => (await api.get('/public/youtube/streams')).data });
  const { data: devotions = [] } = useQuery({ queryKey: ['public-devotions'], queryFn: async () => (await api.get('/public/youtube/devotions?limit=5')).data });
  const [activeDevotionIndex, setActiveDevotionIndex] = useState(0);
  const featured = ytData?.live?.[0] || ytData?.upcoming?.[0] || ytData?.completed?.[0];
  const activeDevotion = devotions[activeDevotionIndex];

  // SEO: Local FAQ + recurring service events
  const FAQS = [
    {
      q: 'Where is Ministry of Jesus church located in Dublin?',
      a: 'Ministry of Jesus meets at 7 Le Fanu Road, Ballyfermot Road, Cherry Orchard, Dublin, D10 NH74. We welcome visitors from across Dublin, Kildare, Meath and Wicklow.',
    },
    {
      q: 'What time is Sunday Worship at Ministry of Jesus?',
      a: 'Our Sunday Worship Service begins every Sunday at 10:30 AM. Sunday School for children runs at 09:30 AM in the same building.',
    },
    {
      q: 'Is there a Pentecostal church near Kildare, Meath or Wicklow?',
      a: 'Yes \u2014 Ministry of Jesus is a Pentecostal church based in Dublin within easy reach of Kildare, Meath and Wicklow. Many of our worshippers travel weekly from Naas, Maynooth, Navan, Bray and surrounding areas.',
    },
    {
      q: 'Do you offer healing prayer or deliverance ministry?',
      a: 'Yes. We hold Deliverance & Healing Prayer every Thursday from 10:00 AM to 12:00 PM at our Dublin church. You can also submit a prayer request online any time.',
    },
    {
      q: 'Can I watch the Sunday Service online?',
      a: 'Absolutely. Our Sunday Worship Service is streamed live every Sunday on our YouTube channel @MinistryofJesus and embedded on our LIVE page.',
    },
    {
      q: 'Do you have a children\u2019s ministry?',
      a: 'Yes \u2014 we run Sunday School every Sunday at 09:30 AM with Garda-vetted volunteers, plus a Summer Vacation Bible School each year for children ages 5\u201314.',
    },
  ];

  const jsonLd = [
    breadcrumbSchema([{ name: 'Home', path: '/' }]),
    faqSchema(FAQS),
    ...BUSINESS.services.map((s) => recurringServiceEvent(s)),
  ];

  return (
    <div>
      <Seo
        title={PAGE_SEO.home.title}
        titleExact
        description={PAGE_SEO.home.description}
        keywords={PAGE_SEO.home.keywords}
        path="/"
        jsonLd={jsonLd}
      />
      {/* HERO */}
      <section className="relative gradient-purple-gold text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at top, rgba(255,224,96,0.4), transparent 60%)' }}></div>
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-moj-gold/10 blur-3xl"></div>
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-moj-teal/10 blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fadeUp">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-moj-gold text-xs font-bold uppercase tracking-wider mb-6 border border-moj-gold/30">
              <Sparkles size={14} /> Pentecostal Church in Ireland
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-white mb-6">
              Welcome to <span className="text-moj-gold">Ministry of Jesus</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-xl">
              A Spirit-filled church family in Ireland, growing in worship, prayer, the Word of God, and the power of the Holy Spirit.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/services" className="btn-gold text-base" data-testid="hero-join-sunday">
                <Heart size={18}/> Join Sunday Service
              </Link>
              <Link to="/live" className="btn-outline border-2 !text-white !border-moj-gold hover:!bg-moj-gold hover:!text-moj-purple-deep text-base" data-testid="hero-watch-live">
                <Play size={18} fill="currentColor"/> Watch LIVE
              </Link>
            </div>
            <div className="mt-10 italic text-moj-gold/90 text-sm border-l-2 border-moj-gold/50 pl-4">
              “{CHURCH.motto}” — {CHURCH.scripture}
            </div>
          </div>

          <div className="relative">
            <div className="relative aspect-square max-w-md mx-auto">
              <div className="absolute inset-0 rounded-full bg-moj-gold/20 blur-3xl"></div>
              <img
                src={LOGO_URL}
                alt="Ministry of Jesus emblem with cross, Bible and Zechariah 4:6"
                className="relative w-full h-full object-contain drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WELCOME */}
      <section className="py-20 bg-moj-cream-soft">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <SectionTitle kicker="You are welcome here" title="A family rooted in Christ" center
            subtitle="Whether you are exploring faith, seeking community, or longing to grow deeper in the Spirit — Ministry of Jesus is a place to belong, believe and become." />
          <div className="grid sm:grid-cols-3 gap-6 mt-12">
            {[
              { icon: BookOpen, title: 'Bible Centered', text: 'God\u2019s Word is at the heart of everything we teach and live.' },
              { icon: Heart, title: 'Spirit-Filled', text: 'We welcome the power, gifts and presence of the Holy Spirit.' },
              { icon: Users, title: 'Family Together', text: 'A community where every age — children, youth, families — belongs.' },
            ].map((c) => (
              <div key={c.title} className="card-moj p-6">
                <div className="w-14 h-14 rounded-full gradient-purple flex items-center justify-center mx-auto mb-4">
                  <c.icon size={24} className="text-moj-gold" />
                </div>
                <h3 className="font-serif text-xl mb-2">{c.title}</h3>
                <p className="text-moj-charcoal/80 text-sm">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS CAROUSEL */}
      <section className="py-20 bg-moj-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle kicker="Coming Up" title="Upcoming Events"
            subtitle="Reserve your seat — we can't wait to worship with you and your family."/>
          <EventsCarousel events={events} />
        </div>
      </section>

      {/* DAILY DEVOTION */}
      <section className="py-20 bg-moj-cream-soft" aria-labelledby="daily-devotion-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <SectionTitle
              kicker="Daily Bread"
              title="Daily Devotion"
              subtitle="A short word from Scripture with Pastor Jayaseelan Vincent — five minutes to start your day in the presence of God."
            />
            <a
              href={CHURCH.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline text-sm py-2 px-4"
              data-testid="devotion-youtube"
            >
              <Youtube size={16}/> Subscribe
            </a>
          </div>
          <h2 id="daily-devotion-heading" className="sr-only">Daily Devotion videos from Ministry of Jesus YouTube</h2>

          {devotions.length > 0 ? (
            <div className="grid lg:grid-cols-[1fr_380px] gap-6">
              {/* Featured player */}
              <article className="card-moj overflow-hidden" data-testid="devotion-featured">
                <div className="relative aspect-video bg-moj-purple-deep">
                  {activeDevotion ? (
                    <iframe
                      key={activeDevotion.video_id}
                      title={decodeEntities(activeDevotion.title)}
                      src={`https://www.youtube.com/embed/${activeDevotion.video_id}?rel=0&modestbranding=1`}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/70">
                      <BookOpen size={48} className="text-moj-gold"/>
                    </div>
                  )}
                </div>
                <div className="p-5 sm:p-6">
                  <div className="inline-flex items-center gap-2 bg-moj-purple/10 text-moj-purple-deep text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                    <BookOpen size={12}/> Daily Devotion
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-moj-purple-deep mb-2 leading-tight">
                    {activeDevotion ? decodeEntities(activeDevotion.title) : 'Loading…'}
                  </h3>
                  {activeDevotion && (
                    <div className="flex items-center gap-3 text-xs text-moj-charcoal/60">
                      <span className="flex items-center gap-1"><Clock size={12}/> {relativeTime(activeDevotion.published_at)}</span>
                      <span>·</span>
                      <span>Ps. Jayaseelan Vincent</span>
                    </div>
                  )}
                </div>
              </article>

              {/* Playlist */}
              <aside aria-label="Recent Daily Devotion videos">
                <h3 className="font-serif text-lg text-moj-purple-deep mb-3 px-1">Recent devotions</h3>
                <ul className="space-y-3" role="list">
                  {devotions.map((d, i) => {
                    const active = i === activeDevotionIndex;
                    return (
                      <li key={d.video_id}>
                        <button
                          type="button"
                          onClick={() => setActiveDevotionIndex(i)}
                          aria-pressed={active}
                          className={`w-full text-left card-moj overflow-hidden flex gap-3 p-2 transition group ${active ? 'ring-2 ring-moj-gold shadow-md' : ''}`}
                          data-testid={`devotion-item-${i}`}
                        >
                          <div className="relative w-32 sm:w-36 flex-shrink-0 aspect-video bg-moj-purple-deep rounded-md overflow-hidden">
                            <img
                              src={d.thumbnail}
                              alt={decodeEntities(d.title)}
                              className="absolute inset-0 w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className={`absolute inset-0 flex items-center justify-center bg-black/30 transition ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                              <div className="w-9 h-9 rounded-full bg-moj-gold flex items-center justify-center shadow-lg">
                                <Play size={16} fill="#2B0038" className="text-moj-purple-deep ml-0.5"/>
                              </div>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0 py-1 pr-1">
                            <div className={`text-sm font-semibold leading-snug line-clamp-2 ${active ? 'text-moj-purple-deep' : 'text-moj-charcoal'}`}>
                              {decodeEntities(d.title).replace(/\s*\|\s*Daily Devotion.*$/i, '')}
                            </div>
                            <div className="text-[11px] text-moj-charcoal/60 mt-1 flex items-center gap-1">
                              <Clock size={10}/> {relativeTime(d.published_at)}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <a
                  href={CHURCH.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 text-sm font-semibold text-moj-purple hover:text-moj-purple-deep mt-4"
                >
                  View all on YouTube <ArrowRight size={14}/>
                </a>
              </aside>
            </div>
          ) : (
            /* Empty state — section stays visible until first Daily Devotion video lands */
            <div className="card-moj p-8 sm:p-12 text-center max-w-3xl mx-auto" data-testid="devotion-empty">
              <div className="w-16 h-16 rounded-full gradient-purple flex items-center justify-center mx-auto mb-5">
                <BookOpen size={28} className="text-moj-gold"/>
              </div>
              <h3 className="font-serif text-2xl text-moj-purple-deep mb-3">New devotions coming soon</h3>
              <p className="text-moj-charcoal/80 mb-6 max-w-xl mx-auto">
                Subscribe to our YouTube channel to be notified the moment Pastor Jayaseelan Vincent shares the next
                Daily Devotion. We post short, Spirit-filled words from Scripture to bless your day.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <a
                  href={CHURCH.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="devotion-empty-subscribe"
                >
                  <Youtube size={18}/> Subscribe on YouTube
                </a>
                <a
                  href={`https://www.youtube.com/@${CHURCH.youtube.split('@').pop()}/videos`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                  data-testid="devotion-empty-browse"
                >
                  Browse all videos <ArrowRight size={16}/>
                </a>
              </div>
              <p className="text-xs text-moj-charcoal/50 mt-6">
                <em>Tip:</em> Upload a video on the channel with <strong>"Daily Devotion"</strong> in its title and it
                will appear here automatically (within 10 minutes).
              </p>
            </div>
          )}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-20 gradient-soft-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle kicker="Ministries" title="Find your place to serve & grow"
            subtitle="Discover the ministries that build up the body of Christ at Ministry of Jesus."/>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { to: '/services/sunday-school', title: 'Sunday School', desc: 'Where children meet Jesus through Bible stories and joy.', icon: BookOpen, color: 'from-purple-500 to-purple-700' },
              { to: '/services/summer-camp', title: 'Summer Camp', desc: 'Unforgettable days of worship, games and the Word.', icon: Sparkles, color: 'from-yellow-500 to-amber-700' },
              { to: '/services/prayer-cell', title: 'Prayer Cell', desc: 'Home gatherings for intercession and breakthrough.', icon: HandHeart, color: 'from-pink-500 to-rose-700' },
              { to: '/services/evangelism', title: 'Evangelism', desc: 'Carrying the good news of Jesus into our community.', icon: Heart, color: 'from-cyan-500 to-blue-700' },
            ].map((s) => (
              <Link key={s.to} to={s.to} className="card-moj p-6 group block" data-testid={`service-card-${s.to.split('/').pop()}`}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-4 group-hover:scale-110 transition`}>
                  <s.icon size={22} className="text-white"/>
                </div>
                <h3 className="font-serif text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-moj-charcoal/80 mb-4">{s.desc}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-moj-purple group-hover:gap-2 transition-all">
                  Learn more <ArrowRight size={16}/>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE PREVIEW */}
      <section className="py-20 gradient-purple text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at 20% 80%, rgba(255,224,96,0.4), transparent 50%)' }}></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle kicker="On Air" title="Watch our LIVE Service" light
              subtitle="Can't make it in person? Join our worship service online from anywhere in the world."/>
            {ytData?.has_live && (
              <span className="live-badge mb-4">Live Now</span>
            )}
            {featured && (
              <div className="mb-6">
                <h3 className="font-serif text-xl text-moj-gold mb-1">{featured.title}</h3>
                <p className="text-white/70 text-sm line-clamp-2">{featured.description}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-4">
              <Link to="/live" className="btn-gold" data-testid="home-watch-live"><Play size={18} fill="currentColor"/> Watch Live Service</Link>
              <a href={CHURCH.youtube} target="_blank" rel="noopener noreferrer" className="btn-outline !text-white !border-moj-gold hover:!bg-moj-gold hover:!text-moj-purple-deep">
                <Youtube size={18}/> Visit YouTube
              </a>
            </div>
          </div>

          <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border-2 border-moj-gold/30">
            {featured?.video_id ? (
              <iframe
                title="Featured Ministry of Jesus video"
                src={`https://www.youtube.com/embed/${featured.video_id}`}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-moj-purple-deep">
                <div className="text-center text-white/70">
                  <Youtube size={48} className="mx-auto mb-3 text-moj-gold"/>
                  <p>Live stream will appear here</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MORNING PRAYER CTA */}
      <section className="py-20 bg-moj-cream-soft">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="card-moj overflow-hidden grid lg:grid-cols-2">
            <div className="gradient-purple-gold p-10 sm:p-14 text-white relative">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-moj-gold/20 blur-3xl"></div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-moj-gold text-xs font-bold uppercase tracking-wider mb-5 border border-moj-gold/30">
                  <Sun size={12}/> Every Monday · 5:00 AM
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl text-white leading-tight mb-3">Begin your week on your knees.</h2>
                <p className="text-white/90 mb-6">Join Ministry of Jesus for Early Morning Prayer over Google Meet — wherever you are in Ireland or the world. One hour of worship, the Word and intercession before the day begins.</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/morning-prayer" className="btn-gold"><Video size={16}/> Join Morning Prayer</Link>
                  <Link to="/morning-prayer" className="btn-outline !text-white !border-moj-gold hover:!bg-moj-gold hover:!text-moj-purple-deep">View Details</Link>
                </div>
              </div>
            </div>
            <div className="p-10 sm:p-14 bg-moj-cream-soft flex flex-col justify-center">
              <h3 className="font-serif text-2xl text-moj-purple-deep mb-4">What to expect</h3>
              <ul className="space-y-3 text-moj-charcoal">
                <li className="flex gap-3"><span className="text-moj-gold-deep">✓</span> Spirit-led worship to set the atmosphere</li>
                <li className="flex gap-3"><span className="text-moj-gold-deep">✓</span> A short Word from Scripture</li>
                <li className="flex gap-3"><span className="text-moj-gold-deep">✓</span> United intercession for Ireland, family & nations</li>
                <li className="flex gap-3"><span className="text-moj-gold-deep">✓</span> Personal prayer requests welcome</li>
              </ul>
              <p className="text-sm italic text-moj-purple-deep mt-5 border-l-2 border-moj-gold pl-4">
                "But they that wait upon the LORD shall renew their strength." — Isaiah 40:31
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LOCAL SEO — Counties Served */}
      <section className="py-20 bg-moj-cream-soft" aria-labelledby="counties-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionTitle
            kicker="A church for the East of Ireland"
            title="Serving Dublin, Kildare, Meath & Wicklow"
            center
            subtitle="Ministry of Jesus is a Pentecostal church based in Cherry Orchard, Dublin — and worshippers travel to us every week from across Dublin and the surrounding counties of Kildare, Meath and Wicklow."
          />
          <h2 id="counties-heading" className="sr-only">Counties we serve in Ireland</h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {[
              {
                county: 'Dublin',
                blurb: 'Our home county. Easy access from Ballyfermot, Cherry Orchard, Inchicore, Clondalkin, Lucan, Tallaght and the city centre.',
                towns: ['Ballyfermot', 'Cherry Orchard', 'Clondalkin', 'Lucan', 'Tallaght', 'City Centre'],
              },
              {
                county: 'Kildare',
                blurb: 'A short drive from Dublin \u2014 we regularly welcome families from across County Kildare to Sunday Worship and prayer ministry.',
                towns: ['Naas', 'Maynooth', 'Celbridge', 'Leixlip', 'Newbridge', 'Kilcock'],
              },
              {
                county: 'Meath',
                blurb: 'Worshippers from Meath are part of our church family \u2014 join us in person on Sundays or online for Morning Prayer and Daily Devotion.',
                towns: ['Navan', 'Ashbourne', 'Dunshaughlin', 'Ratoath', 'Trim', 'Dunboyne'],
              },
              {
                county: 'Wicklow',
                blurb: 'A growing community of believers from Wicklow connect with Ministry of Jesus for worship, healing prayer and family ministry.',
                towns: ['Bray', 'Greystones', 'Wicklow Town', 'Arklow', 'Newtownmountkennedy', 'Blessington'],
              },
            ].map((c) => (
              <article key={c.county} className="card-moj p-6 h-full flex flex-col" data-testid={`county-${c.county.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={20} className="text-moj-gold-deep" />
                  <h3 className="font-serif text-xl text-moj-purple-deep">{c.county}</h3>
                </div>
                <p className="text-sm text-moj-charcoal/80 mb-4 flex-1">{c.blurb}</p>
                <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-2">Towns we welcome</div>
                <ul className="flex flex-wrap gap-1.5">
                  {c.towns.map((t) => (
                    <li key={t} className="text-[11px] bg-moj-purple/5 text-moj-purple-deep px-2 py-0.5 rounded-full border border-moj-purple/10">
                      {t}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <p className="text-center text-sm text-moj-charcoal/70 mt-10 max-w-3xl mx-auto">
            Looking for a <strong>Pentecostal church near Dublin, Kildare, Meath or Wicklow</strong>? You’re very welcome at Ministry of Jesus.
            We meet every Sunday at <strong>10:30 AM</strong> in Cherry Orchard, Dublin 10, with healing prayer on Thursdays and online Morning Prayer every Monday.
          </p>
        </div>
      </section>

      {/* FAQ — drives FAQ rich results */}
      <section className="py-20 bg-moj-cream" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <SectionTitle
            kicker="Frequently asked"
            title="Common questions about visiting Ministry of Jesus"
            center
            subtitle="Whether it’s your first time or you’re travelling from Kildare, Meath or Wicklow — here’s what people often ask before they visit."
          />
          <h2 id="faq-heading" className="sr-only">Frequently asked questions</h2>
          <dl className="mt-10 space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="card-moj p-5 sm:p-6" data-testid={`faq-${f.q.slice(0, 24).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <dt className="font-serif text-lg text-moj-purple-deep mb-2">{f.q}</dt>
                <dd className="text-sm text-moj-charcoal/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* PRAYER CTA */}
      <section className="py-20 bg-moj-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="card-moj p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-moj-gold/20 blur-2xl"></div>
            <HandHeart size={48} className="text-moj-purple mx-auto mb-4"/>
            <h2 className="font-serif text-3xl sm:text-4xl text-moj-purple-deep mb-3">Need prayer? We’re here for you.</h2>
            <p className="text-moj-charcoal/80 mb-8 max-w-2xl mx-auto">
              Send us your prayer request — our intercessors will lift it up with faith and confidence in Jesus.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="prayer-cta"><Heart size={18}/> Submit a Prayer Request</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
